import React, { useMemo } from 'react';
import SchoolIcon from '@mui/icons-material/School';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import setup from '../assets/data/setup.json';
import MotionSection from './ui/MotionSection';
import SectionHeader from './ui/SectionHeader';

function groupByYear<T extends { date?: string }>(items: T[]) {
  const groups = new Map<string, T[]>();

  for (const item of items) {
    const year = item.date?.slice(0, 4) || 'Other';
    const existing = groups.get(year) || [];
    existing.push(item);
    groups.set(year, existing);
  }

  return Array.from(groups.entries()).sort(([a], [b]) => Number(b) - Number(a));
}

type Cert = (typeof setup.certifications)[number] & { progress?: number };

function Education() {
  const certGroups = useMemo(() => groupByYear((setup.certifications || []) as Cert[]), []);

  return (
    <MotionSection id="education" muted>
      <SectionHeader eyebrow="Background" title="Education & Professional Development" />

      <div className="space-y-4 mb-10">
        {setup.education.map((edu) => (
          <div key={edu.institution} className="ui-card flex gap-4 p-5">
            <div className="w-10 h-10 rounded-lg border border-line bg-canvas-muted flex items-center justify-center text-ink-secondary flex-shrink-0">
              <SchoolIcon />
            </div>
            <div>
              <h3 className="font-medium text-ink">
                {edu.degree} in {edu.field}
              </h3>
              <p className="text-sm text-ink-muted">{edu.institution}</p>
              <p className="text-sm text-ink-muted mt-1">
                {edu.duration} · GPA: {edu.grade}
              </p>
            </div>
          </div>
        ))}
      </div>

      {certGroups.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-ink mb-4 tracking-[-0.01em]">
            Professional Development & Certifications
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {certGroups.flatMap(([, certs]) =>
              certs.map((cert) => {
                const isOngoing = cert.status === 'ongoing';
                const progress = cert.progress ?? (cert.status === 'completed' ? 100 : undefined);

                return (
                  <div key={`${cert.name}-${cert.issuer}`} className="ui-card p-5">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-9 h-9 rounded-lg border border-line bg-canvas-muted flex items-center justify-center text-ink-secondary flex-shrink-0">
                        <WorkspacePremiumIcon sx={{ fontSize: 18 }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-[10px] font-bold uppercase tracking-wide text-ink-muted">
                            {cert.issuer}
                          </span>
                          {isOngoing ? (
                            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-accent-muted text-accent">
                              In Progress
                            </span>
                          ) : (
                            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                              Completed
                            </span>
                          )}
                        </div>
                        <h4 className="text-sm font-medium text-ink leading-snug">{cert.name}</h4>
                        <p className="text-xs text-ink-muted mt-1">{cert.date}</p>
                      </div>
                    </div>
                    {progress !== undefined && isOngoing && (
                      <div className="mt-2 pt-2 border-t border-line/60">
                        <div className="flex justify-between text-[10px] text-ink-faint mb-1">
                          <span>In progress</span>
                          <span className="tabular-nums">{progress}%</span>
                        </div>
                        <div className="h-1 rounded-full bg-canvas-muted overflow-hidden">
                          <div
                            className="h-full rounded-full bg-accent/50 transition-all duration-700"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </MotionSection>
  );
}

export default Education;
